/**
 * Scene Timeline Editor - Sprint 15-16
 * Multi-scene recording timeline with drag-to-reorder functionality
 */

import React, { useState, useEffect } from 'react';
import { useAtom } from 'jotai';
import { Card, Button, Badge, Space, Typography, Empty, Modal } from 'antd';
import { 
  PlayCircle, 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  GripVertical,
  Trash2,
  Plus,
  Film,
  Eye,
  RefreshCw,
  Info,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  Moon,
  Sun
} from 'lucide-react';
import { 
  sceneListAtom, 
  currentSceneAtom, 
  activeProjectAtom,
  sceneRecordingStateAtom,
  selectedSceneIdAtom,
  isStitchingModalVisibleAtom,
  isTimelineCollapsedAtom,
  isCinemaModeAtom,
  updateProjectDurationAtom
} from '../../atoms/sceneAtoms';
import { Scene, SceneTransition } from '../../../../types/scene';
import SceneService from '../../services/sceneService';
import { ScenePreviewModal } from '../modals/ScenePreviewModal';
import { TransitionArrow } from './TransitionArrow';
import TransitionStudioModal from './TransitionStudioModal';
import { SceneTimelineInfoModal } from './SceneTimelineInfoModal';
import {
  InlineEditableTitle,
  SceneQuickActionsMenu
} from './SceneCard';
import CopySettingsModal, { CopySettings } from './CopySettingsModal';
import EditSettingsModal from './EditSettingsModal';
import { SceneDetailsModal } from './SceneDetailsModal';
import { FullRecordingPreviewModal } from './FullRecordingPreviewModal';
import FloatingPreviewButton from './FloatingPreviewButton';
import logger from '../../utils/logger';
import './SceneTimelineEditor.scss';

const { Text } = Typography;

interface SceneTimelineEditorProps {
  onSceneSelect?: (scene: Scene) => void;
  onSceneDelete?: (sceneId: string) => void;
  onAddScene?: () => void;
  readOnly?: boolean;
}

const formatDuration = (milliseconds: number): string => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const SceneTimelineEditor: React.FC<SceneTimelineEditorProps> = ({
  onSceneSelect,
  onSceneDelete,
  onAddScene,
  readOnly = false,
}) => {
  const [sceneList, setSceneList] = useAtom(sceneListAtom);
  const [currentScene] = useAtom(currentSceneAtom);
  const [activeProject, setActiveProject] = useAtom(activeProjectAtom);
  const [recordingState] = useAtom(sceneRecordingStateAtom);
  const [selectedSceneId, setSelectedSceneId] = useAtom(selectedSceneIdAtom);
  const [, setIsStitchingModalVisible] = useAtom(isStitchingModalVisibleAtom);
  const [, updateProjectDuration] = useAtom(updateProjectDurationAtom);
  
  // Sprint 19+: Timeline collapse and cinema mode
  const [isCollapsed, setIsCollapsed] = useAtom(isTimelineCollapsedAtom);
  const [isCinemaMode, setIsCinemaMode] = useAtom(isCinemaModeAtom);
  
  const [previewScene, setPreviewScene] = useState<Scene | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  
  // Unified Transition Studio state (replaces separate edit/preview modals)
  const [transitionStudioSceneId, setTransitionStudioSceneId] = useState<string | null>(null);

  // Info modal state
  const [showInfoModal, setShowInfoModal] = useState(false);
  
  // Scene Details Modal state (Sprint 19+ Redesign)
  const [detailsModalScene, setDetailsModalScene] = useState<Scene | null>(null);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Copy Settings Modal state
  const [showCopySettingsModal, setShowCopySettingsModal] = useState(false);
  const [copySettingsSourceScene, setCopySettingsSourceScene] = useState<Scene | null>(null);
  
  // Edit Settings Modal state
  const [showEditSettingsModal, setShowEditSettingsModal] = useState(false);
  const [editSettingsScene, setEditSettingsScene] = useState<Scene | null>(null);
  
  // Full Recording Preview state (Sprint 20+)
  const [showFullRecordingPreview, setShowFullRecordingPreview] = useState(false);

  // Fix Durations state
  const [isFixingDurations, setIsFixingDurations] = useState(false);

  // Drag and Drop state
  const [draggedSceneId, setDraggedSceneId] = useState<string | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);

  useEffect(() => {
    if (currentScene) {
      setSelectedSceneId(currentScene.id);
    }
  }, [currentScene, setSelectedSceneId]);

  // Auto-collapse timeline when recording starts, auto-expand when stopped
  useEffect(() => {
    if (recordingState.isRecording) {
      setIsCollapsed(true);
    }
  }, [recordingState.isRecording, setIsCollapsed]);

  const handleSceneClick = (scene: Scene, e?: React.MouseEvent) => {
    // Prevent event bubbling to avoid unintended clicks on parent elements
    e?.stopPropagation();
    e?.preventDefault();
    
    if (!readOnly) {
      setSelectedSceneId(scene.id);
      if (onSceneSelect) {
        logger.info(`[SceneTimelineEditor] Scene clicked: ${scene.title} - Status: ${scene.status}`);
        onSceneSelect(scene);
      }
    }
  };

  const handleTransitionChange = async (sceneId: string, transition: SceneTransition) => {
    if (!activeProject) return;
    
    try {
      const updatedScenes = sceneList.map(s => 
        s.id === sceneId ? { ...s, transitionOut: transition } : s
      );
      
      await SceneService.updateScene(sceneId, { transitionOut: transition });
      setSceneList(updatedScenes);
      
      logger.info(`[SceneTimelineEditor] Transition updated for scene ${sceneId}:`, transition);
    } catch (error) {
      logger.error('[SceneTimelineEditor] Failed to update transition:', error);
    }
  };

  // Opens unified Transition Studio modal (handles both preview + editing)
  const handleOpenTransitionStudio = (sceneId: string) => {
    setTransitionStudioSceneId(sceneId);
    logger.info('[SceneTimelineEditor] Opening Transition Studio for scene:', sceneId);
  };

  const handleApplyTransitionToAll = async (transition: SceneTransition) => {
    if (!activeProject) return;
    
    try {
      const updatedScenes = sceneList.map(s => ({ ...s, transitionOut: transition }));
      
      // Update all scenes in database
      await Promise.all(
        sceneList.map(s => SceneService.updateScene(s.id, { transitionOut: transition }))
      );
      
      setSceneList(updatedScenes);
      setTransitionStudioSceneId(null);
      
      logger.info('[SceneTimelineEditor] Applied transition to all scenes:', transition);
      Modal.success({
        title: 'Success',
        content: `Applied ${transition.type} transition to all ${sceneList.length} scenes`,
        okText: 'OK',
      });
    } catch (error) {
      logger.error('[SceneTimelineEditor] Failed to apply transition to all:', error);
      Modal.error({
        title: 'Error',
        content: 'Failed to apply transition to all scenes. Please try again.',
        okText: 'OK',
      });
    }
  };

  const handleApplyTransitionToRemaining = async (sceneId: string, transition: SceneTransition) => {
    if (!activeProject) return;
    
    try {
      const currentIndex = sceneList.findIndex(s => s.id === sceneId);
      const remainingScenes = sceneList.slice(currentIndex);
      
      const updatedScenes = sceneList.map(s => 
        remainingScenes.some(rs => rs.id === s.id) 
          ? { ...s, transitionOut: transition }
          : s
      );
      
      // Update remaining scenes in database
      await Promise.all(
        remainingScenes.map(s => SceneService.updateScene(s.id, { transitionOut: transition }))
      );
      
      setSceneList(updatedScenes);
      setTransitionStudioSceneId(null);
      
      logger.info('[SceneTimelineEditor] Applied transition to remaining scenes:', transition);
      Modal.success({
        title: 'Success',
        content: `Applied ${transition.type} transition to ${remainingScenes.length} remaining scenes`,
        okText: 'OK',
      });
    } catch (error) {
      logger.error('[SceneTimelineEditor] Failed to apply transition to remaining:', error);
      Modal.error({
        title: 'Error',
        content: 'Failed to apply transition to remaining scenes. Please try again.',
        okText: 'OK',
      });
    }
  };

  const handleFixDurations = async () => {
    if (!activeProject) {
      Modal.error({
        title: 'No Project Found',
        content: 'No active project found. Please select a project first.',
      });
      return;
    }

    const scenesWithZeroDuration = sceneList.filter(s => s.status === 'recorded' && s.duration === 0);
    
    if (scenesWithZeroDuration.length === 0) {
      Modal.info({
        title: 'All Scenes OK',
        content: 'All recorded scenes already have proper durations.',
      });
      return;
    }

    Modal.confirm({
      title: 'Fix Scene Durations?',
      content: (
        <div>
          <p>Found {scenesWithZeroDuration.length} scene(s) with missing durations:</p>
          <ul style={{ marginTop: 8, marginBottom: 0 }}>
            {scenesWithZeroDuration.map(s => (
              <li key={s.id}>{s.title}</li>
            ))}
          </ul>
          <p style={{ marginTop: 12, marginBottom: 0 }}>
            This will calculate the actual duration from the video data. This may take a few seconds.
          </p>
        </div>
      ),
      okText: 'Fix Durations',
      cancelText: 'Cancel',
      onOk: async () => {
        setIsFixingDurations(true);
        try {
          console.log('🔧 Starting duration fix...');
          const result = await SceneService.fixSceneDurations(activeProject.id);
          
          // Reload project to refresh state
          const updatedProject = await SceneService.getProjectById(activeProject.id);
          if (updatedProject) {
            setSceneList(updatedProject.scenes);
            setActiveProject(updatedProject);
          }

          if (result.fixed > 0) {
            Modal.success({
              title: 'Durations Fixed!',
              content: `Successfully fixed ${result.fixed} scene(s). ${result.skipped > 0 ? `${result.skipped} skipped.` : ''} ${result.failed > 0 ? `${result.failed} failed.` : ''}`,
            });
          } else if (result.failed > 0) {
            Modal.error({
              title: 'Fix Failed',
              content: `Failed to fix ${result.failed} scene(s). ${result.skipped} were skipped. Check the console for details.`,
            });
          } else {
            Modal.info({
              title: 'No Changes',
              content: `${result.skipped} scene(s) were skipped (already have durations or no video data).`,
            });
          }
        } catch (error) {
          console.error('❌ Duration fix error:', error);
          Modal.error({
            title: 'Fix Failed',
            content: `Failed to fix durations: ${error instanceof Error ? error.message : 'Unknown error'}`,
          });
        } finally {
          setIsFixingDurations(false);
        }
      },
    });
  };

  const handleDeleteScene = async (sceneId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!activeProject || readOnly) return;
    
    try {
      await SceneService.removeSceneFromProject(activeProject.id, sceneId);
      const updatedScenes = sceneList.filter(s => s.id !== sceneId);
      setSceneList(updatedScenes);
      
      if (onSceneDelete) {
        onSceneDelete(sceneId);
      }
    } catch (error) {
      console.error('Failed to delete scene:', error);
    }
  };

  const handlePreviewScene = (scene: Scene, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (scene.status !== 'recorded') {
      logger.warn('[SceneTimelineEditor] Cannot preview scene that is not recorded');
      return;
    }
    
    setPreviewScene(scene);
    setShowPreviewModal(true);
    logger.info('[SceneTimelineEditor] Opening preview for scene:', scene.title);
  };

  const handleReRecordScene = async (scene: Scene, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (scene.status !== 'recorded' || readOnly) return;
    
    Modal.confirm({
      title: 'Re-record Scene?',
      content: `This will delete the existing recording for "${scene.title}". Are you sure you want to continue?`,
      okText: 'Re-record',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await SceneService.deleteSceneChunks(scene.id);
          
          const updatedScenes = sceneList.map(s => 
            s.id === scene.id 
              ? { ...s, status: 'pending' as Scene['status'], duration: 0, videoChunks: [] }
              : s
          );
          setSceneList(updatedScenes);
          
          logger.info('[SceneTimelineEditor] Scene reset for re-recording:', scene.title);
        } catch (error) {
          logger.error('[SceneTimelineEditor] Failed to reset scene:', error);
          Modal.error({
            title: 'Error',
            content: 'Failed to reset scene for re-recording. Please try again.',
          });
        }
      },
    });
  };

  const getSceneStatusIcon = (status: Scene['status']) => {
    switch (status) {
      case 'recorded':
        return <CheckCircle size={16} color="#52c41a" />;
      case 'recording':
        return <PlayCircle size={16} color="#ff4d4f" />;
      case 'failed':
        return <AlertCircle size={16} color="#ff4d4f" />;
      case 'pending':
      default:
        return <Clock size={16} color="#8c8c8c" />;
    }
  };

  const getSceneStatusBadge = (status: Scene['status']) => {
    switch (status) {
      case 'recorded':
        return <Badge status="success" text="Recorded" />;
      case 'recording':
        return <Badge status="processing" text="Recording" />;
      case 'failed':
        return <Badge status="error" text="Failed" />;
      case 'pending':
      default:
        return <Badge status="default" text="Pending" />;
    }
  };

  // ==================== Enhanced Scene Card Handlers (Sprint 19+) ====================

  const handleTitleChange = async (sceneId: string, newTitle: string) => {
    try {
      await SceneService.updateScene(sceneId, { title: newTitle });
      const updatedScenes = sceneList.map(s => 
        s.id === sceneId ? { ...s, title: newTitle } : s
      );
      setSceneList(updatedScenes);
      logger.info('[SceneTimelineEditor] Scene title updated:', { sceneId, newTitle });
    } catch (error) {
      logger.error('[SceneTimelineEditor] Failed to update scene title:', error);
    }
  };

  const handleNotesChange = async (sceneId: string, notes: string) => {
    try {
      await SceneService.updateSceneNotes(sceneId, notes);
      const updatedScenes = sceneList.map(s => 
        s.id === sceneId ? { ...s, notes } : s
      );
      setSceneList(updatedScenes);
      logger.info('[SceneTimelineEditor] Scene notes updated:', { sceneId });
    } catch (error) {
      logger.error('[SceneTimelineEditor] Failed to update scene notes:', error);
    }
  };

  const handleTagsChange = async (sceneId: string, tags: any[]) => {
    try {
      await SceneService.updateSceneTags(sceneId, tags);
      const updatedScenes = sceneList.map(s => 
        s.id === sceneId ? { ...s, tags } : s
      );
      setSceneList(updatedScenes);
      logger.info('[SceneTimelineEditor] Scene tags updated:', { sceneId, tags });
    } catch (error) {
      logger.error('[SceneTimelineEditor] Failed to update scene tags:', error);
    }
  };

  const handleViewDetails = (scene: Scene) => {
    setDetailsModalScene(scene);
    setShowDetailsModal(true);
    logger.info('[SceneTimelineEditor] Opening details modal for scene:', scene.title);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setDetailsModalScene(null);
  };

  const handleDetailsPrevious = () => {
    if (!detailsModalScene) return;
    const currentIndex = sceneList.findIndex(s => s.id === detailsModalScene.id);
    if (currentIndex > 0) {
      setDetailsModalScene(sceneList[currentIndex - 1]);
    }
  };

  const handleDetailsNext = () => {
    if (!detailsModalScene) return;
    const currentIndex = sceneList.findIndex(s => s.id === detailsModalScene.id);
    if (currentIndex < sceneList.length - 1) {
      setDetailsModalScene(sceneList[currentIndex + 1]);
    }
  };

  const handleDuplicateScene = async (sceneId: string) => {
    if (!activeProject || readOnly) return;

    try {
      const maxOrder = Math.max(...sceneList.map(s => s.order), -1);
      const duplicatedScene = await SceneService.duplicateScene(sceneId, maxOrder + 1);
      
      // Update project's scenes array
      const updatedScenes = [...activeProject.scenes, duplicatedScene];
      await SceneService.updateProject(activeProject.id, { scenes: updatedScenes });
      
      setSceneList([...sceneList, duplicatedScene]);
      
      logger.info('[SceneTimelineEditor] Scene duplicated:', { original: sceneId, duplicate: duplicatedScene.id });
      Modal.success({
        title: 'Scene Duplicated',
        content: `"${duplicatedScene.title}" has been added to the timeline.`,
      });
    } catch (error) {
      logger.error('[SceneTimelineEditor] Failed to duplicate scene:', error);
      Modal.error({
        title: 'Error',
        content: 'Failed to duplicate scene. Please try again.',
      });
    }
  };

  const handleCopySettings = (sceneId: string) => {
    const scene = sceneList.find(s => s.id === sceneId);
    if (scene) {
      setCopySettingsSourceScene(scene);
      setShowCopySettingsModal(true);
      logger.info('[SceneTimelineEditor] Opening copy settings modal for:', sceneId);
    }
  };

  const handleCopySettingsConfirm = async (targetSceneIds: string[], settings: CopySettings) => {
    if (!copySettingsSourceScene) return;

    const sourceScene = copySettingsSourceScene;
    const updatePromises = targetSceneIds.map(async (targetId) => {
      const updates: Partial<Scene> = {};
      
      if (settings.layout && sourceScene.layoutPreset) {
        updates.layoutPreset = sourceScene.layoutPreset;
      }
      
      if (settings.background && sourceScene.background) {
        updates.background = sourceScene.background;
      }
      
      if (settings.script && sourceScene.script) {
        updates.script = sourceScene.script;
      }

      await SceneService.updateScene(targetId, updates);
      return { targetId, updates };
    });

    await Promise.all(updatePromises);

    // Update local state
    const updatedScenes = sceneList.map(scene => {
      if (targetSceneIds.includes(scene.id)) {
        const updates: Partial<Scene> = {};
        
        if (settings.layout && sourceScene.layoutPreset) {
          updates.layoutPreset = sourceScene.layoutPreset;
        }
        
        if (settings.background && sourceScene.background) {
          updates.background = sourceScene.background;
        }
        
        if (settings.script && sourceScene.script) {
          updates.script = sourceScene.script;
        }

        return { ...scene, ...updates };
      }
      return scene;
    });

    setSceneList(updatedScenes);
    logger.info('[SceneTimelineEditor] Settings copied to scenes:', targetSceneIds);
  };

  const handleJumpToRecording = (sceneId: string) => {
    const scene = sceneList.find(s => s.id === sceneId);
    if (scene) {
      handleSceneClick(scene);
      logger.info('[SceneTimelineEditor] Jumped to scene for recording:', sceneId);
    }
  };

  const handleEditSettings = (sceneId: string) => {
    const scene = sceneList.find(s => s.id === sceneId);
    if (scene) {
      setEditSettingsScene(scene);
      setShowEditSettingsModal(true);
      logger.info('[SceneTimelineEditor] Opening edit settings modal for:', sceneId);
    }
  };

  const handleEditSettingsSave = async (sceneId: string, updates: Partial<Scene>) => {
    await SceneService.updateScene(sceneId, updates);

    // Update local state
    const updatedScenes = sceneList.map(scene =>
      scene.id === sceneId ? { ...scene, ...updates } : scene
    );

    setSceneList(updatedScenes);
    logger.info('[SceneTimelineEditor] Settings updated for scene:', sceneId);
  };

  // ==================== Drag and Drop Handlers ====================
  
  const handleDragStart = (e: React.DragEvent, sceneId: string) => {
    setDraggedSceneId(sceneId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', sceneId);
    
    logger.info('[SceneTimelineEditor] Drag started:', sceneId);
  };

  const handleDragEnd = () => {
    setDraggedSceneId(null);
    setDropTargetIndex(null);
    logger.info('[SceneTimelineEditor] Drag ended');
  };

  const handleDragOver = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedSceneId) {
      setDropTargetIndex(targetIndex);
    }
  };

  const handleDragLeave = () => {
    // Don't clear immediately to prevent flickering
    setTimeout(() => {
      setDropTargetIndex(null);
    }, 50);
  };

  const handleDrop = async (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    
    if (!draggedSceneId || !activeProject) {
      setDraggedSceneId(null);
      setDropTargetIndex(null);
      return;
    }

    const draggedIndex = sceneList.findIndex(s => s.id === draggedSceneId);
    
    if (draggedIndex === -1 || draggedIndex === targetIndex) {
      setDraggedSceneId(null);
      setDropTargetIndex(null);
      return;
    }

    try {
      // Reorder scenes array
      const reorderedScenes = [...sceneList];
      const [draggedScene] = reorderedScenes.splice(draggedIndex, 1);
      
      // Insert at new position
      const insertIndex = targetIndex > draggedIndex ? targetIndex : targetIndex;
      reorderedScenes.splice(insertIndex, 0, draggedScene);
      
      // Update order values
      const updatedScenes = reorderedScenes.map((scene, index) => ({
        ...scene,
        order: index
      }));

      // Update local state immediately for smooth UX
      setSceneList(updatedScenes);

      // Update database in background
      await Promise.all(
        updatedScenes.map((scene, index) =>
          SceneService.updateScene(scene.id, { order: index })
        )
      );

      logger.info('[SceneTimelineEditor] Scenes reordered:', {
        from: draggedIndex,
        to: targetIndex,
        sceneId: draggedSceneId
      });
    } catch (error) {
      logger.error('[SceneTimelineEditor] Failed to reorder scenes:', error);
      Modal.error({
        title: 'Reorder Failed',
        content: 'Failed to reorder scenes. Please try again.',
      });
    } finally {
      setDraggedSceneId(null);
      setDropTargetIndex(null);
    }
  };

  const handleExportScene = async (sceneId: string) => {
    const scene = sceneList.find(s => s.id === sceneId);
    if (!scene) {
      logger.error('[SceneTimelineEditor] Scene not found for export:', sceneId);
      return;
    }

    if (scene.videoChunks.length === 0) {
      Modal.warning({
        title: 'No Recording',
        content: 'This scene has not been recorded yet. Please record the scene before exporting.',
      });
      return;
    }

    try {
      logger.info('[SceneTimelineEditor] Starting scene export:', sceneId);
      
      // Import chunk DB dynamically
      const { db } = await import('../../idb/chunk-db');
      
      // Get video chunks from IndexedDB
      const chunkPromises = scene.videoChunks.map(async (chunkId) => {
        const chunk = await db.chunks.get(chunkId);
        if (!chunk) {
          throw new Error(`Chunk not found: ${chunkId}`);
        }
        return chunk;
      });

      const chunks = await Promise.all(chunkPromises);
      
      // Sort by chunkNo to ensure proper order
      chunks.sort((a, b) => a.chunkNo - b.chunkNo);

      // Convert chunks to blobs
      const blobs = chunks.map(chunk => 
        chunk.blob instanceof Blob ? chunk.blob : new Blob([chunk.blob as ArrayBuffer])
      );

      // Combine into single blob
      const combinedBlob = new Blob(blobs, { type: 'video/webm' });

      // Generate filename
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `${scene.title.replace(/[^a-z0-9]/gi, '_')}_${timestamp}.webm`;

      // Trigger download
      const url = URL.createObjectURL(combinedBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Clean up
      setTimeout(() => URL.revokeObjectURL(url), 100);

      logger.info('[SceneTimelineEditor] Scene exported successfully:', filename);
      
      Modal.success({
        title: 'Export Complete',
        content: `Scene "${scene.title}" has been exported as ${filename}`,
      });
    } catch (error) {
      logger.error('[SceneTimelineEditor] Scene export failed:', error);
      Modal.error({
        title: 'Export Failed',
        content: `Failed to export scene: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }
  };

  const handleResetVideo = async (sceneId: string) => {
    const scene = sceneList.find(s => s.id === sceneId);
    if (!scene) {
      logger.error('[SceneTimelineEditor] Scene not found for reset:', sceneId);
      return;
    }

    if (scene.status !== 'recorded') {
      Modal.warning({
        title: 'No Recording',
        content: 'This scene has not been recorded yet.',
      });
      return;
    }

    Modal.confirm({
      title: 'Reset Scene Video?',
      content: `This will delete the recorded video for "${scene.title}" but keep all scene settings (script, background, layout). You can record this scene again. Are you sure?`,
      okText: 'Reset Video',
      okType: 'danger',
      cancelText: 'Cancel',
      okButtonProps: {
        danger: true
      },
      onOk: async () => {
        try {
          logger.info('[SceneTimelineEditor] Resetting scene video:', sceneId);
          
          // Delete video chunks from IndexedDB
          if (scene.videoChunks && scene.videoChunks.length > 0) {
            const { db } = await import('../../idb/chunk-db');
            await Promise.all(
              scene.videoChunks.map(chunkId => db.chunks.delete(chunkId))
            );
            logger.info('[SceneTimelineEditor] Deleted video chunks:', scene.videoChunks.length);
          }
          
          // Update scene status to pending and clear video-related fields
          await SceneService.updateScene(
            sceneId,
            {
              status: 'pending' as const,
              videoUrl: undefined,
              thumbnailUrl: scene.thumbnailUrl, // Preserve thumbnail
              videoChunks: [],
              duration: 0,
              recordedAt: undefined,
              analysisStatus: 'pending', // Reset analysis status for re-recording
            },
            activeProject?.id
          );
          
          // Update local state
          const updatedScenes = sceneList.map(s =>
            s.id === sceneId
              ? {
                  ...s,
                  status: 'pending' as const,
                  videoUrl: undefined,
                  videoChunks: [],
                  duration: 0,
                  recordedAt: undefined,
                  analysisStatus: 'pending', // Reset analysis status for re-recording
                }
              : s
          );
          setSceneList(updatedScenes);
          
          logger.info('[SceneTimelineEditor] Scene video reset successfully');
          
          Modal.success({
            title: 'Video Reset',
            content: `The recorded video for "${scene.title}" has been deleted. All scene settings have been preserved.`,
          });
        } catch (error) {
          logger.error('[SceneTimelineEditor] Failed to reset scene video:', error);
          Modal.error({
            title: 'Reset Failed',
            content: `Failed to reset scene video: ${error instanceof Error ? error.message : 'Unknown error'}`,
          });
        }
      }
    });
  };

  if (sceneList.length === 0) {
    return (
      <div className="scene-timeline-empty">
        <Empty
          image={<Film size={48} color="#8c8c8c" />}
          description={
            <Space direction="vertical" size="small">
              <Text type="secondary">No scenes in timeline</Text>
              {!readOnly && onAddScene && (
                <Button type="primary" icon={<Plus size={16} />} onClick={onAddScene}>
                  Add Scene
                </Button>
              )}
            </Space>
          }
        />
      </div>
    );
  }

  // Ensure all scenes have IDs (fallback for old data without IDs)
  const sortedScenes = [...sceneList].sort((a, b) => a.order - b.order).map(scene => ({
    ...scene,
    id: scene.id || `scene-fallback-${scene.order}-${Date.now()}`
  }));

  return (
    <div className={`scene-timeline-editor ${isCinemaMode ? 'cinema-mode' : ''}`}>
      <div className="scene-timeline-header">
        <Space align="center" size="middle">
          <div className="dadan-brand-logo">
            <Film size={24} style={{ color: '#1890ff' }} />
          </div>
          <div className="timeline-title-group">
            <Space size={8} align="center">
              <Text strong style={{ fontSize: 18, background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                Scene Timeline
              </Text>
              <Badge count={sceneList.length} style={{ backgroundColor: '#1890ff' }} />
              <Button
                type="text"
                icon={<Info size={16} />}
                onClick={() => setShowInfoModal(true)}
                className="timeline-info-button"
                title="Learn about multi-scene storytelling"
              />
            </Space>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
              Professional Multi-Scene Recording
            </Text>
          </div>
        </Space>
        
        <Space size={8} align="center">
          {!readOnly && onAddScene && (
            <Button 
              type="dashed" 
              icon={<Plus size={16} />} 
              onClick={onAddScene}
              size="small"
            >
              Add Scene
            </Button>
          )}
          
          <Button
            type="text"
            size="small"
            icon={isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? "Expand timeline" : "Collapse timeline"}
            className="timeline-toggle-button"
          />
          
          <Button
            type="text"
            size="small"
            icon={isCinemaMode ? <Sun size={16} /> : <Moon size={16} />}
            onClick={() => setIsCinemaMode(!isCinemaMode)}
            title={isCinemaMode ? "Switch to light mode" : "Switch to cinema mode"}
            className="cinema-mode-toggle-button"
          />
        </Space>
      </div>

      {/* Conditional rendering: Mini-mode vs Full-mode */}
      {isCollapsed ? (
        // MINI-MODE: Compact horizontal strip
        <div className="scene-timeline-mini-mode">
          <div className="mini-mode-track">
            {sortedScenes.map((scene, index) => {
              const isSelected = selectedSceneId === scene.id;
              const isCurrent = currentScene?.id === scene.id;
              
              const getStatusColor = (status: Scene['status']) => {
                switch (status) {
                  case 'recorded':
                    return '#52c41a';
                  case 'recording':
                    return '#faad14';
                  case 'failed':
                    return '#ff4d4f';
                  case 'pending':
                  default:
                    return '#8c8c8c';
                }
              };
              
              return (
                <div
                  key={scene.id || `mini-scene-${index}`}
                  className={`mini-scene-thumbnail ${isSelected ? 'selected' : ''} ${isCurrent ? 'current' : ''}`}
                  onClick={(e) => handleSceneClick(scene, e)}
                  title={`${scene.title} - ${scene.status}`}
                >
                  {scene.thumbnail ? (
                    <img src={scene.thumbnail} alt={scene.title} />
                  ) : (
                    <div className="mini-thumbnail-placeholder">
                      {getSceneStatusIcon(scene.status)}
                    </div>
                  )}
                  
                  <div className="mini-scene-number">#{scene.order + 1}</div>
                  
                  <div 
                    className="mini-status-dot" 
                    style={{ backgroundColor: getStatusColor(scene.status) }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        // FULL-MODE: Detailed timeline view
        <div className="scene-timeline-container">
          <div className="scene-timeline-track">
            {(() => {
              // Calculate latest recorded scene index
              const recordedScenes = sortedScenes.filter(s => s.status === 'recorded');
              const latestRecordedScene = recordedScenes[recordedScenes.length - 1];
              const latestRecordedIndex = latestRecordedScene 
                ? sortedScenes.findIndex(s => s.id === latestRecordedScene.id)
                : -1;

              return sortedScenes.map((scene, index) => {
                const isSelected = selectedSceneId === scene.id;
                const isCurrent = currentScene?.id === scene.id;
                const isBeingDragged = draggedSceneId === scene.id;
                const hasNextScene = index < sortedScenes.length - 1;
                const isLatestRecorded = index === latestRecordedIndex && recordedScenes.length > 0;
                const showDropIndicatorBefore = dropTargetIndex === index && draggedSceneId && draggedSceneId !== scene.id;
                const showDropIndicatorAfter = dropTargetIndex === index + 1 && draggedSceneId && hasNextScene;
                
                return (
                  <React.Fragment key={scene.id || `scene-fragment-${index}`}>
                {/* Drop indicator BEFORE scene */}
                {showDropIndicatorBefore && (
                  <div className="drop-indicator" />
                )}
                
                <div
                  key={scene.id ? `scene-card-${scene.id}` : `scene-card-index-${index}`}
                  className={`scene-card ${isSelected ? 'selected' : ''} ${isCurrent ? 'current' : ''} ${isBeingDragged ? 'dragging' : ''} ${recordingState.isRecording && isCurrent ? 'recording-active' : ''}`}
                  draggable={!readOnly && !recordingState.isRecording}
                  onDragStart={(e) => handleDragStart(e, scene.id)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onClick={(e) => handleSceneClick(scene, e)}
                >
                  <Card
                    size="small"
                    variant={isSelected ? "outlined" : "borderless"}
                    className="scene-card-inner"
                    cover={
                      scene.thumbnail ? (
                        <div className="scene-thumbnail">
                          <img src={scene.thumbnail} alt={scene.title} />
                        </div>
                      ) : (
                        <div className="scene-thumbnail-placeholder">
                          {getSceneStatusIcon(scene.status)}
                        </div>
                      )
                    }
                  >
                    {/* Quick Actions - Top Right Corner */}
                    <div className="scene-card-quick-actions-corner">
                      <div className="quick-actions-group">
                        <div 
                          className="view-details-btn" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(scene);
                          }}
                          title="View scene details"
                        >
                          <Lightbulb size={16} />
                        </div>
                        <SceneQuickActionsMenu
                          scene={scene}
                          onDuplicate={handleDuplicateScene}
                          onCopySettings={handleCopySettings}
                          onJumpToRecording={handleJumpToRecording}
                          onEditSettings={handleEditSettings}
                          onExport={handleExportScene}
                          onResetVideo={handleResetVideo}
                          onDelete={(id) => handleDeleteScene(id, {} as any)}
                          disabled={readOnly}
                        />
                      </div>
                    </div>

                    {/* Simplified Scene Card - Compact View */}
                    <div className="scene-card-header-compact">
                      <div className="scene-header-top">
                        <div className="scene-status-group">
                          {getSceneStatusBadge(scene.status)}
                          <Text strong className="scene-order">
                            #{scene.order + 1}
                          </Text>
                        </div>
                      </div>
                      
                      <InlineEditableTitle
                        value={scene.title}
                        onChange={(newTitle) => handleTitleChange(scene.id, newTitle)}
                        disabled={readOnly}
                        placeholder="Untitled Scene"
                      />
                    </div>
                    
                    {/* Mini Duration Indicators */}
                    <div className="scene-duration-indicator">
                      <span className="duration-actual">
                        {scene.status === 'recorded' && scene.recordedAt 
                          ? formatDuration(scene.duration || 0)
                          : '--:--'}
                      </span>
                      <span className="duration-separator">/</span>
                      <span className="duration-suggested">
                        {scene.suggestedDuration ? formatDuration(scene.suggestedDuration) : '--:--'}
                      </span>
                    </div>
                    
                    {/* Custom Actions Footer */}
                    {!readOnly && (
                      <div className="scene-card-actions-custom">
                        <div className="scene-action-left">
                          <div 
                            className={`scene-preview-btn ${scene.status !== 'recorded' ? 'disabled' : ''}`}
                            onClick={(e) => scene.status === 'recorded' && handlePreviewScene(scene, e)}
                            title={scene.status === 'recorded' ? "Preview scene" : "Preview available after recording"}
                          >
                            <Eye size={18} />
                          </div>
                          <div 
                            className={`scene-rerecord-btn ${scene.status !== 'recorded' ? 'disabled' : ''}`}
                            onClick={(e) => scene.status === 'recorded' && handleReRecordScene(scene, e)}
                            title={scene.status === 'recorded' ? "Re-record scene" : "Re-record available after recording"}
                          >
                            <RefreshCw size={18} />
                          </div>
                        </div>
                        <div className="scene-action-right">
                          <div 
                            className="scene-drag-handle" 
                            title="Drag to reorder"
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            <GripVertical size={18} />
                          </div>
                          <div 
                            className="scene-delete-btn" 
                            onClick={(e) => handleDeleteScene(scene.id, e)}
                            title="Delete scene"
                          >
                            <Trash2 size={18} />
                          </div>
                        </div>
                      </div>
                    )}
                  </Card>
                </div>
              
              {/* Transition Arrow between scenes */}
              {hasNextScene && (
                <div 
                  className="transition-arrow-wrapper"
                  style={{ 
                    position: 'relative',
                    flex: '0 0 100px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                  }}
                >
                  <TransitionArrow
                    transition={scene.transitionOut}
                    onPreview={() => handleOpenTransitionStudio(scene.id)}
                    onEdit={() => handleOpenTransitionStudio(scene.id)}
                    disabled={readOnly || recordingState.isRecording}
                    cinemaMode={isCinemaMode}
                  />
                </div>
              )}

              {/* Floating Preview Button - appears after latest recorded scene */}
              {isLatestRecorded && (
                <FloatingPreviewButton
                  recordedCount={recordedScenes.length}
                  totalScenes={sortedScenes.length}
                  totalDuration={formatDuration(recordedScenes.reduce((sum, s) => sum + s.duration, 0))}
                  onClick={() => setShowFullRecordingPreview(true)}
                  cinemaMode={isCinemaMode}
                />
              )}
            </React.Fragment>
                );
              });
            })()}
        </div>
      </div>
      )}

      <div className="scene-timeline-footer">
        <Space size="large" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <Text type="secondary">
                Total Duration: {formatDuration(sceneList.reduce((sum, s) => sum + s.duration, 0))}
              </Text>
              <Text type="secondary">|</Text>
              <Text type="secondary">
                Recorded: {sceneList.filter(s => s.status === 'recorded').length} / {sceneList.length}
              </Text>
            </Space>
            
            {/* Visual Progress Bar */}
            <div className="scene-progress-bar">
              <div className="progress-segments">
                {sceneList.map(scene => (
                  <div 
                    key={scene.id}
                    className={`progress-segment ${scene.status}`}
                    style={{ width: `${100 / sceneList.length}%` }}
                  />
                ))}
              </div>
              <div className="progress-label">
                {Math.round((sceneList.filter(s => s.status === 'recorded').length / sceneList.length) * 100)}% Complete
              </div>
            </div>
          </Space>
          
          <Space>
            {/* Fix Durations button - show if there are recorded scenes with duration=0 */}
            {sceneList.some(s => s.status === 'recorded' && s.duration === 0) && (
              <Button 
                type="default" 
                icon={<RefreshCw size={16} />}
                onClick={handleFixDurations}
                loading={isFixingDurations}
                size="large"
                title="Fix missing scene durations"
              >
                Fix Durations
              </Button>
            )}
            
            {sceneList.length > 0 && sceneList.filter(s => s.status === 'recorded').length === sceneList.length && (
              <Button 
                type="primary" 
                icon={<Film size={16} />}
                onClick={() => setIsStitchingModalVisible(true)}
                size="large"
              >
                Stitch & Export
              </Button>
            )}
          </Space>
        </Space>
      </div>

      <ScenePreviewModal
        scene={previewScene}
        scenes={sceneList}
        visible={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setPreviewScene(null);
        }}
      />

      {/* Unified Transition Studio Modal (replaces separate edit/preview modals) */}
      {transitionStudioSceneId && (() => {
        const studioScene = sceneList.find(s => s.id === transitionStudioSceneId);
        const studioIndex = sceneList.findIndex(s => s.id === transitionStudioSceneId);
        const nextScene = studioIndex + 1 < sceneList.length ? sceneList[studioIndex + 1] : undefined;
        const beforeSceneDuration = studioScene?.duration;
        const afterSceneDuration = nextScene?.duration;
        
        // Render modal even if nextScene exists but isn't recorded - allow transition editing
        // The modal handles missing videos gracefully with placeholders
        return studioScene && nextScene && (
          <TransitionStudioModal
            visible={true}
            transition={studioScene.transitionOut || { type: 'cut', duration: 1.0, easing: 'ease-in-out' }}
            fromScene={studioScene}
            toScene={nextScene}
            projectId={activeProject?.id}
            onClose={() => setTransitionStudioSceneId(null)}
            onChange={(transition) => handleTransitionChange(transitionStudioSceneId, transition)}
            onApplyToAll={handleApplyTransitionToAll}
            onApplyToRemaining={(transition) => handleApplyTransitionToRemaining(transitionStudioSceneId, transition)}
            isFirstTransition={studioIndex === 0}
            isLastTransition={studioIndex === sceneList.length - 1}
            beforeSceneDuration={beforeSceneDuration}
            afterSceneDuration={afterSceneDuration}
            disabled={readOnly || recordingState.isRecording}
          />
        );
      })()}

      <SceneTimelineInfoModal
        visible={showInfoModal}
        onClose={() => setShowInfoModal(false)}
      />

      <CopySettingsModal
        visible={showCopySettingsModal}
        sourceScene={copySettingsSourceScene}
        allScenes={sceneList}
        onClose={() => {
          setShowCopySettingsModal(false);
          setCopySettingsSourceScene(null);
        }}
        onCopy={handleCopySettingsConfirm}
      />

      <EditSettingsModal
        visible={showEditSettingsModal}
        scene={editSettingsScene}
        onClose={() => {
          setShowEditSettingsModal(false);
          setEditSettingsScene(null);
        }}
        onSave={handleEditSettingsSave}
      />

      <SceneDetailsModal
        visible={showDetailsModal}
        scene={detailsModalScene}
        onClose={handleCloseDetailsModal}
        onTagsChange={(tags) => detailsModalScene && handleTagsChange(detailsModalScene.id, tags)}
        onNotesChange={(notes) => detailsModalScene && handleNotesChange(detailsModalScene.id, notes)}
        onPrevious={handleDetailsPrevious}
        onNext={handleDetailsNext}
        hasPrevious={detailsModalScene ? sceneList.findIndex(s => s.id === detailsModalScene.id) > 0 : false}
        hasNext={detailsModalScene ? sceneList.findIndex(s => s.id === detailsModalScene.id) < sceneList.length - 1 : false}
        readOnly={readOnly}
      />

      {/* Full Recording Preview Modal (Sprint 20+) */}
      <FullRecordingPreviewModal
        visible={showFullRecordingPreview}
        scenes={sortedScenes}
        onClose={() => setShowFullRecordingPreview(false)}
        cinemaMode={isCinemaMode}
        projectId={activeProject?.id}
      />
    </div>
  );
};

export default SceneTimelineEditor;
